<template>
  <v-container>
    <!-- Header band -->
    <div class="topbar">
      <div class="topbar__row">
        <v-btn
          icon
          variant="text"
          :title="$filters.capitalize($t('machine.back_wind'))"
          :to="{ name: 'wind_production' }"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div :class="['topbar__badge', status ? (status.running ? 'topbar__badge--on' : 'topbar__badge--off') : '']">
          <v-icon size="22">mdi-wind-turbine</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">
            {{ producerName || $filters.capitalize($t("production.name")) }}
          </div>
          <div class="topbar__sub">
            <template v-if="producer">
              {{ producer.power_station_name }} &middot; {{ producer.producer_type }}
            </template>
            <template v-if="status">
              &middot; {{ $filters.capitalize($t(status.running ? "production.running" : "production.stopped")) }}
            </template>
            <template v-if="lastRefresh">
              &middot; {{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}
            </template>
          </div>
        </div>
        <div class="topbar__actions">
          <v-btn-toggle
            v-model="range"
            mandatory
            density="compact"
            variant="outlined"
            color="primary"
            class="range-toggle"
            @update:model-value="loadTelemetries"
          >
            <v-btn v-for="r in ranges" :key="r.value" :value="r.value" size="small">{{ r.label }}</v-btn>
          </v-btn-toggle>
          <v-btn
            v-if="producer_id"
            variant="tonal"
            size="small"
            prepend-icon="mdi-chart-line"
            :href="grafanaWindProducerUrl(producer_id, range)"
            target="_blank"
            rel="noopener"
          >
            Grafana
          </v-btn>
          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="load"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <template v-if="producer_id">
      <!-- Status KPIs -->
      <v-row v-if="status" dense class="kpi-row">
        <v-col cols="6" md="3">
          <v-card flat class="kpi kpi--blue">
            <div class="kpi__badge"><v-icon size="20">mdi-flash</v-icon></div>
            <div class="min-width-0">
              <div class="kpi__label">{{ $filters.capitalize($t("production.power")) }}</div>
              <div class="kpi__value">
                {{ $filters.formatFloat(status.power) }}
                <span class="kpi__unit">{{ $t("unity.kw") }}</span>
              </div>
              <div class="kpi__hint">
                <template v-if="hasValue(status.theoretical_prod)">
                  {{ $t("production.theoretical") }} {{ $filters.formatFloat(status.theoretical_prod) }} {{ $t("unity.kw") }}
                </template>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="kpi kpi--purple">
            <div class="kpi__badge"><v-icon size="20">mdi-weather-windy</v-icon></div>
            <div class="min-width-0">
              <div class="kpi__label">{{ $filters.capitalize($t("production.wind")) }}</div>
              <div class="kpi__value">
                {{ $filters.formatFloat(status.wind) }}
                <span class="kpi__unit">{{ $t("unity.ms") }}</span>
              </div>
              <div class="kpi__hint">
                <template v-if="hasValue(status.wind_direction)">{{ Math.round(status.wind_direction) }}°</template>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="kpi kpi--green">
            <div class="kpi__badge"><v-icon size="20">mdi-check-decagram</v-icon></div>
            <div class="min-width-0">
              <div class="kpi__label">{{ $filters.capitalize($t("production.dispo")) }} / {{ $t("production.dispo_temps") }}</div>
              <div class="kpi__value">
                {{ $filters.formatPercentage(status.dispo) }}
                <span class="kpi__unit">/ {{ $filters.formatPercentage(status.dispo_temps) }} %</span>
              </div>
              <div class="kpi__hint">{{ $t("production.cumulative", 1) }}</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="kpi" :class="status.current_underprod || hasAlarm(status) ? 'kpi--red' : 'kpi--grey'">
            <div class="kpi__badge"><v-icon size="20">mdi-alert-circle-outline</v-icon></div>
            <div class="min-width-0">
              <div class="kpi__label">{{ $filters.capitalize($t("generic.status")) }}</div>
              <div class="kpi__value kpi__value--small">
                <span v-if="status.limitation_ongoing" class="flag"><v-icon size="small">mdi-tortoise</v-icon> {{ $t("production.limited") }}</span>
                <span v-if="hasAlarm(status)" class="flag text-error"><v-icon size="small">mdi-bell-alert</v-icon> {{ $t("production.alarm_ongoing") }}</span>
                <span v-if="status.current_underprod" class="flag text-warning"><v-icon size="small">mdi-speedometer-slow</v-icon> {{ $t("production.underproduction_ongoing") }}</span>
                <span v-if="!status.limitation_ongoing && !hasAlarm(status) && !status.current_underprod" class="text-success">OK</span>
              </div>
              <div class="kpi__hint">
                <template v-if="status.last_underprod">{{ $t("production.last_underproduction") }} {{ status.last_underprod }}</template>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Grafana panels -->
      <v-row dense class="mt-1">
        <v-col cols="12">
          <v-card flat class="graf-card">
            <div class="graf-card__head">
              <v-icon size="small" class="mr-2" color="primary">mdi-chart-line</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t("machine.power_wind_chart")) }}</span>
            </div>
            <GrafanaFrame
              v-if="GRAFANA_EMBED_ENABLED"
              class="graf-frame"
              :src="grafanaWindProducerPanelUrl(producer_id, GRAFANA_WIND_PANELS.producer_power_wind, range)"
              :href="grafanaWindProducerUrl(producer_id, range)"
              :title="$t('machine.power_wind_chart')"
              :hint="$t('machine.open_grafana')"
            ></GrafanaFrame>
            <a v-else :href="grafanaWindProducerUrl(producer_id, range)" target="_blank" rel="noopener" class="graf-placeholder">
              <v-icon size="32" color="primary">mdi-chart-line</v-icon>
              <span>{{ $t("machine.open_grafana") }}</span>
              <span class="text-caption text-medium-emphasis">{{ $t("machine.embed_disabled") }}</span>
            </a>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card flat class="graf-card">
            <div class="graf-card__head">
              <v-icon size="small" class="mr-2" color="primary">mdi-chart-bell-curve-cumulative</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t("machine.power_curves_chart")) }}</span>
            </div>
            <GrafanaFrame
              v-if="GRAFANA_EMBED_ENABLED"
              class="graf-frame graf-frame--tall"
              :src="grafanaWindProducerPanelUrl(producer_id, GRAFANA_WIND_PANELS.producer_curves, range)"
              :href="grafanaWindProducerUrl(producer_id, range)"
              :title="$t('machine.power_curves_chart')"
              :hint="$t('machine.open_grafana')"
            ></GrafanaFrame>
            <a v-else :href="grafanaWindProducerUrl(producer_id, range)" target="_blank" rel="noopener" class="graf-placeholder">
              <v-icon size="32" color="primary">mdi-chart-bell-curve-cumulative</v-icon>
              <span>{{ $t("machine.open_grafana") }}</span>
            </a>
          </v-card>
        </v-col>
      </v-row>

      <!-- Latest telemetries -->
      <v-card flat class="table-card">
        <div class="table-card__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-table</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ $filters.capitalize($t("machine.latest_telemetries")) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ telemetries.length }}</v-chip>
          <span class="text-caption text-medium-emphasis ml-2">{{ rangeLabel }}</span>
        </div>
        <v-data-table
          class="tel-table"
          :headers="headers"
          :items="telemetries"
          :items-per-page="20"
          :loading="telemetriesLoading"
          density="compact"
          :no-data-text="$filters.capitalize($t('production.no_telemetry'))"
        >
          <template v-slot:[`item.time`]="{ item }">
            <span class="text-no-wrap">{{ $filters.formatDateTime(item.time) }}</span>
          </template>
          <template v-slot:[`item.power`]="{ item }">
            <span class="font-weight-medium">{{ $filters.formatFloat(item.power) }}</span>
            <span class="text-caption text-medium-emphasis"> {{ $t("unity.kw") }}</span>
          </template>
          <template v-slot:[`item.wind`]="{ item }">
            <template v-if="hasValue(item.wind)">{{ $filters.formatFloat(item.wind) }} <span class="text-caption text-medium-emphasis">{{ $t("unity.ms") }}</span></template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
          <template v-slot:[`item.wind_direction`]="{ item }">
            <template v-if="hasValue(item.wind_direction)">{{ Math.round(item.wind_direction) }}°</template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
          <template v-slot:[`item.dispo`]="{ item }">
            <template v-if="hasValue(item.dispo)">{{ $filters.formatFloat(item.dispo) }} %</template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
          <template v-slot:[`item.time_dispo`]="{ item }">
            <template v-if="hasValue(item.time_dispo)">{{ $filters.formatFloat(item.time_dispo) }} %</template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
          <template v-slot:[`item.outdoor_temperature`]="{ item }">
            <template v-if="hasValue(item.outdoor_temperature)">{{ $filters.formatFloat(item.outdoor_temperature) }} °C</template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <v-card v-else flat class="empty-card text-medium-emphasis text-body-2">
      <v-icon size="36" class="mb-2" color="grey">mdi-wind-turbine-alert</v-icon>
      <div>{{ $filters.capitalize($t("machine.producer_not_found")) }}</div>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import {
  isNone,
  grafanaWindProducerUrl,
  grafanaWindProducerPanelUrl,
  GRAFANA_WIND_PANELS,
  GRAFANA_EMBED_ENABLED
} from "@/functions.js";
import utils from "@/mixins/utils";
import GrafanaFrame from "@/components/GrafanaFrame.vue";

const RANGE_HOURS = { "now-24h": 24, "now-7d": 24 * 7, "now-30d": 24 * 30 };

export default {
  components: { GrafanaFrame },
  mixins: [utils],

  computed: {
    producer_id() {
      const id = Number(this.$route.query.producer_id);
      return Number.isFinite(id) && id > 0 ? id : null;
    },

    producer() {
      if (this.telemetries.length && this.telemetries[0].producer) {
        return this.telemetries[0].producer;
      }
      if (this.status) {
        return {
          display_name: this.status.display_name,
          power_station_name: this.status.power_station_name,
          producer_type: this.status.producer_type
        };
      }
      return null;
    },

    producerName() {
      return this.producer ? this.$filters.capitalize(this.producer.display_name || "") : "";
    },

    ranges() {
      const h = this.$t("limitations.duration_units.hour");
      const d = this.$t("limitations.duration_units.day");
      return [
        { value: "now-24h", label: `24 ${h}` },
        { value: "now-7d", label: `7 ${d}` },
        { value: "now-30d", label: `30 ${d}` }
      ];
    },

    rangeLabel() {
      const r = this.ranges.find((x) => x.value === this.range);
      return r ? r.label : "";
    },

    headers() {
      const all = [
        { title: this.$filters.capitalize(this.$t("generic.date")), key: "time", sortable: true },
        { title: this.$filters.capitalize(this.$t("telemetry.power")), key: "power", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("telemetry.wind")), key: "wind", sortable: true, align: "end" },
        { title: "Dir.", key: "wind_direction", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("production.dispo")), key: "dispo", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("production.dispo_temps")), key: "time_dispo", sortable: true, align: "end" },
        { title: "T° ext.", key: "outdoor_temperature", sortable: true, align: "end" }
      ];
      if (this.$vuetify.display.smAndDown) {
        return all.filter((h) => ["time", "power", "wind"].includes(h.key));
      }
      return all;
    }
  },

  methods: {
    grafanaWindProducerUrl,
    grafanaWindProducerPanelUrl,

    hasValue(value) {
      return !isNone(value) && value !== "";
    },

    hasAlarm(item) {
      return !!item.alarm_ongoing && Object.keys(item.alarm_ongoing).length !== 0;
    },

    loadStatus() {
      return this.$store
        .dispatch("telemetries/windCurrentFujin", { ids: [this.producer_id] })
        .then((items) => {
          this.status = Array.isArray(items) && items.length ? items[0] : null;
        })
        .catch((error) => {
          console.error("wind status:", error);
          this.status = null;
        });
    },

    loadTelemetries() {
      this.telemetriesLoading = true;
      return this.$store
        .dispatch("telemetries/windLatestTelemetries", {
          producer_id: this.producer_id,
          hours: RANGE_HOURS[this.range] || 24
        })
        .then((items) => {
          this.telemetries = items;
        })
        .catch((error) => {
          console.error("wind telemetries:", error);
          this.telemetries = [];
        })
        .finally(() => {
          this.telemetriesLoading = false;
        });
    },

    load() {
      if (isNone(this.producer_id)) {
        this.status = null;
        this.telemetries = [];
        return;
      }
      this.loading = true;
      Promise.allSettled([this.loadStatus(), this.loadTelemetries()]).then(() => {
        this.loading = false;
        this.lastRefresh = moment();
      });
    }
  },

  watch: {
    producer_id() {
      this.load();
    }
  },

  mounted() {
    this.load();
    this.intervalId = window.setInterval(() => {
      this.load();
    }, 10 * 60 * 1000);
  },

  unmounted() {
    window.clearInterval(this.intervalId);
  },

  data() {
    return {
      intervalId: undefined,
      loading: false,
      telemetriesLoading: false,
      lastRefresh: null,
      range: "now-24h",
      status: null,
      telemetries: [],
      GRAFANA_WIND_PANELS,
      GRAFANA_EMBED_ENABLED
    };
  }
};
</script>

<style scoped>
/* Sticky header, pinned under the app bar while the page scrolls */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #1f9fd1 55%, #6b5b4f 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 8px;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #dff3f9;
  color: #0093bf;
}
.topbar__badge--on {
  background: #e3f4e4;
  color: #2e7d32;
}
.topbar__badge--off {
  background: #fde7e7;
  color: #d32f2f;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__sub {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}
.range-toggle {
  border-color: rgba(0, 0, 0, 0.12);
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}
.min-width-0 {
  min-width: 0;
}

/* KPI tiles */
.kpi-row {
  margin-top: 10px;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi--purple .kpi__badge {
  background: #ece6f7;
  color: #6a3fb5;
}
.kpi--green .kpi__badge {
  background: #e3f4e4;
  color: #2e7d32;
}
.kpi--red .kpi__badge {
  background: #fde7e7;
  color: #d32f2f;
}
.kpi--grey .kpi__badge {
  background: #eeeeee;
  color: #616161;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__value--small {
  font-size: 0.95rem;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  white-space: normal;
}
.flag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  min-height: 1.1em;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Grafana panels */
.graf-card {
  padding: 12px 16px 10px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.graf-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.graf-frame {
  display: block;
  width: 100%;
  height: 300px;
  border: 0;
  border-radius: 8px;
  background: #fafbfc;
}
.graf-frame--tall {
  height: 360px;
}
.graf-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 140px;
  border-radius: 8px;
  border: 1px dashed rgba(0, 147, 191, 0.4);
  background: #f7fbfd;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  text-decoration: none;
}
.graf-placeholder:hover {
  background: #eef7fb;
}

/* Telemetry table */
.table-card {
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.table-card__head {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
}
.tel-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55) !important;
  background: #fff;
}
.tel-table :deep(td) {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
.tel-table :deep(tbody tr:hover td) {
  background-color: #f2f9fc;
}
.empty-card {
  margin-top: 10px;
  padding: 32px 16px;
  text-align: center;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
}

@media (max-width: 700px) {
  .topbar__row {
    flex-wrap: wrap;
  }
  .topbar__actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}

@media (max-width: 700px) {
  .graf-frame {
    height: 220px !important;
  }
  .topbar__actions {
    flex-wrap: wrap;
    gap: 6px;
  }
  .range-toggle {
    width: 100%;
  }
  .range-toggle .v-btn {
    flex: 1;
  }
}
</style>
